import React, {useEffect, useRef, useState} from 'react';
import {motion, useInView, useReducedMotion} from 'motion/react';
import {
  ArrowRightIcon,
  ArrowUpIcon,
  CalendarDaysIcon,
  CheckIcon,
  DocumentTextIcon,
  PlayIcon,
} from '@heroicons/react-v2/24/outline';
import {
  CheckBadgeIcon,
  PlayIcon as PlaySolidIcon,
  StarIcon as StarSolidIcon,
} from '@heroicons/react-v2/24/solid';
import {BrandMark} from '../Brand';
import AceAvatar from '../chat/AceAvatar';

export const AceBadge = ({className = 'h-9 w-9 rounded-xl'}) => (
  <span className={`inline-flex shrink-0 items-center justify-center bg-indigo-100 ${className}`}>
    <BrandMark className="h-5 w-5" />
  </span>
);

// Mock interview thumbnails of real candidates (see images/homepage/mocks).
// Machine Learning still uses the Contentful course thumbnail until a mock
// thumbnail is provided for it.
const courseShelf = [
  {
    role: 'Product Management',
    gradient: 'bg-gradient-to-br from-indigo-600 to-violet-900',
    thumb: '/images/homepage/mocks/product-management.jpg',
  },
  {
    role: 'Software Engineering',
    gradient: 'bg-gradient-to-br from-sky-700 to-indigo-950',
    thumb: '/images/homepage/mocks/software-engineering.jpg',
  },
  {
    role: 'Data Science',
    gradient: 'bg-gradient-to-br from-emerald-600 to-teal-950',
    thumb: '/images/homepage/mocks/data-science.jpg',
  },
  {
    role: 'Machine Learning',
    gradient: 'bg-gradient-to-br from-fuchsia-700 to-purple-950',
    thumb: 'https://images.ctfassets.net/x78yjrjc11pq/6UfkHxUyLQVfDHSfLNQtWa/1be1539a65b4f6f15201f5b573c0a578/ML_Engineer_Interview_Prep_Course.jpeg?w=288&h=256&fit=fill&q=80&fm=webp',
  },
];

const courseLessons = [
  {icon: PlayIcon, title: 'The STAR framework', meta: 'Behavioral · 8 min'},
  {icon: DocumentTextIcon, title: 'Designing Instagram', meta: 'System design · 12 min'},
];

/**
 * Course mockup: a shelf of course covers trailing off-card to suggest the
 * breadth of the library, over a couple of framework lessons.
 */
export const CoursePreview = () => (
  <div className="mx-auto max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl shadow-indigo-950/10">
    <div className="scrollbar-hidden flex snap-x gap-3 overflow-x-auto px-5 pb-2 pt-5 sm:[mask-image:linear-gradient(90deg,black_78%,transparent)]">
      {courseShelf.map(({role, gradient, thumb}) => (
        <div
          key={role}
          className={`relative flex h-32 w-36 shrink-0 flex-col justify-between overflow-hidden rounded-xl p-3 ${gradient}`}
        >
          <img
            src={thumb}
            alt=""
            className="absolute inset-0 h-full w-full object-cover saturate-[0.9]"
            loading="lazy"
          />
          <span
            className="absolute inset-0 bg-gray-500 opacity-30 mix-blend-multiply"
            aria-hidden="true"
          />
          <span
            className="absolute inset-0 bg-gradient-to-t from-gray-950/70 via-transparent to-transparent"
            aria-hidden="true"
          />
          <span className="relative flex h-7 w-7 items-center justify-center rounded-full bg-white/25 text-white backdrop-blur-sm">
            <PlaySolidIcon className="ml-px h-3 w-3" />
          </span>
          <span className="relative text-pretty text-xs font-semibold leading-snug text-white">
            {role}
            <span className="mt-0.5 block text-[10px] font-medium text-white/70">
              Mock Interview
            </span>
          </span>
        </div>
      ))}
    </div>
    <ul className="divide-y divide-gray-100">
      {courseLessons.map(({icon: Icon, title, meta}) => (
        <li key={title} className="flex items-center gap-3 px-5 py-3">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
            <Icon className="h-4 w-4" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold text-gray-900">{title}</span>
            <span className="mt-0.5 block text-xs text-gray-500">{meta}</span>
          </span>
        </li>
      ))}
    </ul>
  </div>
);

const rubric = [
  {
    skill: 'Structure', score: '4.5', bar: 'bg-emerald-500', text: 'text-emerald-600', width: 'w-[90%]',
  },
  {
    skill: 'Communication', score: '3.8', bar: 'bg-amber-400', text: 'text-amber-600', width: 'w-[76%]',
  },
  {
    skill: 'Depth', score: '2.9', bar: 'bg-rose-400', text: 'text-rose-600', width: 'w-[58%]',
  },
];

/**
 * Mock-interview mockup: live interview stage with a floating rubric
 * card showing the graded feedback members get after each session.
 */
export const InterviewPreview = () => (
  <div className="relative mx-auto max-w-md pb-14 sm:pb-6 sm:pr-8">
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl shadow-indigo-950/10">
      <div className="relative flex flex-col items-center bg-gray-50/80 px-6 pb-9 pt-10 text-center">
        <span className="absolute right-4 top-3.5 flex items-center gap-1.5 text-[11px] font-medium text-gray-400">
          <span className="h-1.5 w-1.5 rounded-full bg-red-400 motion-safe:animate-pulse" />
          12:04
        </span>
        <AceBadge className="h-14 w-14 rounded-full ring-4 ring-indigo-50" />
        <strong className="mt-4 text-base font-semibold text-gray-900">
          Let’s talk through your approach.
        </strong>
        <p className="mt-1 text-sm text-gray-500">
          How would you improve the airport experience?
        </p>
        <div className="mt-4 flex h-10 items-center gap-[3px]" aria-hidden="true">
          {Array.from({length: 27}, (_, i) => (
            <i
              key={`wave-${i}`}
              className="waveform-bar block w-[3px] rounded-full bg-indigo-400"
              style={{height: `${8 + ((i * 13) % 28)}px`, animationDelay: `${i * 0.07}s`}}
            />
          ))}
        </div>
        <span className="mt-1 text-xs font-medium text-gray-400">Listening to your answer</span>
      </div>
    </div>
    <div className="absolute -bottom-2 right-0 w-56 rotate-2 rounded-xl border border-gray-200 bg-white p-4 shadow-2xl shadow-indigo-950/20 sm:-right-3 sm:bottom-2">
      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-400">
        Feedback rubric
      </p>
      <ul className="mt-3 space-y-2.5">
        {rubric.map(({
          skill, score, bar, text, width,
        }) => (
          <li key={skill}>
            <div className="flex items-baseline justify-between text-xs font-medium text-gray-700">
              {skill}
              <span className={`font-semibold ${text}`}>{score}</span>
            </div>
            <div className="mt-1 h-1.5 rounded-full bg-gray-100">
              <span className={`block h-full rounded-full ${bar} ${width}`} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

/**
 * Ace chat mockup that plays out when scrolled into view: the question
 * appears, Ace "thinks" with a typing indicator, then the plan lands.
 * Stages: 0 hidden · 1 question · 2 thinking · 3 answer + plan. The
 * server renders the finished conversation (stage 3) so nothing is
 * hidden from crawlers; the sequence only arms below the fold.
 */
export const ChatPreview = () => {
  const reducedMotion = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, {once: true, amount: 0.45});
  const [stage, setStage] = useState(3);
  const armed = useRef(false);

  useEffect(() => {
    const rect = ref.current?.getBoundingClientRect();
    if (!reducedMotion && rect && rect.top > window.innerHeight) {
      armed.current = true;
      setStage(0);
    }
  }, [reducedMotion]);

  useEffect(() => {
    if (!armed.current || !inView) return undefined;
    const timers = [
      setTimeout(() => setStage(1), 300),
      setTimeout(() => setStage(2), 850),
      setTimeout(() => setStage(3), 2000),
    ];
    return () => timers.forEach(clearTimeout);
  }, [inView]);

  const pop = (visible) => ({opacity: visible ? 1 : 0, y: visible ? 0 : 10});

  return (
    <div
      ref={ref}
      className="mx-auto max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white pb-4 shadow-xl shadow-indigo-950/10"
    >
      <div className="flex items-center gap-3 border-b border-gray-100 px-5 py-3">
        <AceAvatar className="h-8 w-8" />
        <strong className="text-sm font-semibold text-gray-900">Ace</strong>
      </div>
      <motion.div
        className="ml-16 mr-5 mt-4 rounded-2xl rounded-br-md bg-indigo-600 px-4 py-2.5 text-sm leading-relaxed text-white"
        initial={false}
        animate={pop(stage >= 1)}
        transition={{duration: 0.35, ease: 'easeOut'}}
      >
        I have a product interview in two weeks. Where should I start?
      </motion.div>
      <div className="mx-5 mt-3.5 flex gap-3">
        <motion.span
          initial={false}
          animate={{opacity: stage >= 2 ? 1 : 0}}
          transition={{duration: 0.25}}
        >
          <AceAvatar className="h-7 w-7" />
        </motion.span>
        {/* The answer stays in flow so the slot keeps a constant height;
            the typing dots overlay it while Ace "thinks". */}
        <span className="relative min-w-0 flex-1">
          <motion.span
            className="absolute left-0 top-0 inline-flex items-center gap-1 rounded-2xl rounded-bl-md bg-gray-100 px-3.5 py-3"
            initial={false}
            animate={{opacity: stage === 2 ? 1 : 0}}
            transition={{duration: 0.25}}
            aria-hidden={stage !== 2}
          >
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400"
                style={{animationDelay: `${i * 0.15}s`}}
              />
            ))}
          </motion.span>
          <motion.p
            className="text-sm leading-relaxed text-gray-600"
            initial={false}
            animate={pop(stage >= 3)}
            transition={{duration: 0.35, ease: 'easeOut'}}
          >
            <strong className="font-semibold text-gray-900">Let’s make a plan. </strong>
            We’ll focus on product sense, analytical thinking, and telling your story.
          </motion.p>
        </span>
      </div>
      <motion.div
        className="mx-5 ml-15 mt-3.5 rounded-xl border border-gray-200 px-4 py-3"
        initial={false}
        animate={pop(stage >= 3)}
        transition={{duration: 0.4, delay: stage >= 3 ? 0.2 : 0, ease: 'easeOut'}}
      >
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-400">
          Your next steps
        </p>
        {[
          'Build your product sense foundation',
          'Practice a real interview question',
          'Put it together in a mock interview',
        ].map((item, i) => (
          <div key={item} className="mt-2.5 flex items-center gap-2.5 text-sm text-gray-700">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-indigo-50 text-xs font-semibold text-indigo-600">
              {i + 1}
            </span>
            <span className="min-w-0 flex-1 truncate">{item}</span>
            <CheckIcon className="h-4 w-4 shrink-0 text-indigo-400" />
          </div>
        ))}
      </motion.div>
      <motion.a
        className="mx-5 mt-3.5 flex items-center justify-between rounded-xl border border-gray-200 px-4 py-2 text-sm text-gray-400 transition hover:border-indigo-300"
        href="/chat"
        initial={false}
        animate={{opacity: stage >= 3 ? 1 : 0}}
        transition={{duration: 0.4, delay: stage >= 3 ? 0.35 : 0}}
      >
        Ask Ace anything…
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-600 text-white">
          <ArrowUpIcon className="h-4 w-4" />
        </span>
      </motion.a>
    </div>
  );
};

const practiceTracks = {
  Engineering: {
    items: [
      {
        icon: 'google', title: 'Two Sum', meta: 'Arrays · Easy',
      },
      {
        icon: 'meta', title: 'Design a URL shortener', meta: 'System design · Medium',
      },
      {
        icon: 'anthropic', title: 'Design a rate limiter', meta: 'System design · Medium',
      },
    ],
  },
  Product: {
    items: [
      {
        icon: 'google', title: 'Improve Google Maps', meta: 'Product sense · Medium',
      },
      {
        icon: 'meta', title: 'Improve Instagram Reels', meta: 'Product sense · Medium',
      },
      {
        icon: 'doordash', title: 'Grow DashPass subscriptions', meta: 'Execution · Hard',
      },
    ],
  },
  Data: {
    items: [
      {
        icon: 'meta', title: 'Design an A/B test', meta: 'Experimentation · Medium',
      },
      {
        icon: 'reddit', title: 'Measure feed engagement', meta: 'Metrics · Medium',
      },
      {
        icon: 'openai', title: 'Design evals for a chatbot', meta: 'ML systems · Hard',
      },
    ],
  },
};

/**
 * Practice mockup: a role toggle over a playlist of real questions, each
 * with a video answer from a real candidate.
 */
export const PracticeMock = () => {
  const [track, setTrack] = useState('Engineering');
  const current = practiceTracks[track];
  return (
    <div className="mx-auto max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white text-left shadow-xl shadow-indigo-950/10">
      <div className="flex items-center gap-2 px-5 pb-3.5 pt-4">
        {Object.keys(practiceTracks).map((name) => (
          <button
            key={name}
            type="button"
            aria-pressed={track === name}
            onClick={() => setTrack(name)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
              track === name
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
          >
            {name}
          </button>
        ))}
      </div>
      <ul className="divide-y divide-gray-100 border-t border-gray-100">
        {current.items.map(({icon, title, meta}) => (
          <li key={title} className="flex items-center gap-3 px-5 py-3.5">
            <img src={`/images/companies/icons/${icon}.svg`} alt="" className="h-5 w-5 shrink-0" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold text-gray-900">{title}</span>
              <span className="mt-0.5 block text-xs text-gray-500">{meta}</span>
            </span>
            <ArrowRightIcon className="ml-auto h-4 w-4 shrink-0 text-gray-300" />
          </li>
        ))}
      </ul>
    </div>
  );
};

const experiences = [
  {
    company: 'Apple',
    icon: 'apple',
    role: 'Software Engineer · 5 rounds',
    excerpt:
      '“The onsite was two coding rounds, a system design deep-dive, and a behavioral with the hiring manager. Expect follow-ups on trade-offs.”',
  },
  {
    company: 'Google',
    icon: 'google',
    role: 'Product Manager · 4 rounds',
    excerpt:
      '“Product sense and analytical rounds back to back. My interviewer pushed hard on prioritization and how I’d measure success.”',
  },
  {
    company: 'Anthropic',
    icon: 'anthropic',
    role: 'Software Engineer · 4 rounds',
    excerpt:
      '“Very practical loop: a coding screen, system design, and a conversation about how I work with AI tools. Thoughtful interviewers throughout.”',
  },
];

/**
 * A stack of firsthand interview experience cards that cycles through
 * companies, the front card rotating to the back every few seconds.
 */
export const ExperienceStack = () => {
  const reducedMotion = useReducedMotion();
  const [front, setFront] = useState(0);
  useEffect(() => {
    if (reducedMotion) return undefined;
    const id = setInterval(() => setFront((i) => (i + 1) % experiences.length), 5200);
    return () => clearInterval(id);
  }, [reducedMotion]);

  return (
    <div className="relative mx-auto h-64 max-w-md pt-10 sm:h-60" aria-live="off">
      {experiences.map((item, i) => {
        const offset = (i - front + experiences.length) % experiences.length;
        return (
          <motion.article
            key={item.company}
            className="absolute inset-x-0 top-10 rounded-2xl border border-gray-200 bg-white p-5 shadow-xl shadow-indigo-950/10"
            initial={false}
            animate={{
              y: offset * -18,
              scale: 1 - offset * 0.045,
              opacity: offset === 0 ? 1 : 0.8 - offset * 0.25,
              zIndex: experiences.length - offset,
            }}
            transition={reducedMotion ? {duration: 0} : {duration: 0.9, ease: [0.22, 1, 0.36, 1]}}
          >
            <div className="flex items-center gap-3">
              <img
                src={`/images/companies/icons/${item.icon}.svg`}
                alt=""
                className="h-8 w-8"
                loading="lazy"
              />
              <div className="min-w-0">
                <strong className="block truncate text-sm font-semibold text-gray-900">
                  {`${item.company} interview experience`}
                </strong>
                <small className="block text-xs text-gray-500">{item.role}</small>
              </div>
              <span className="ml-auto shrink-0 rounded-md bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-700">
                Offer
              </span>
            </div>
            <p className="mt-3 text-pretty text-sm leading-relaxed text-gray-600">
              {item.excerpt}
            </p>
          </motion.article>
        );
      })}
    </div>
  );
};

const jobListings = [
  {
    icon: 'google', role: 'Software Engineer', team: 'Engineering · Referral available',
  },
  {
    icon: 'openai', role: 'Forward-Deployed Engineer', team: 'Engineering',
  },
  {
    icon: 'doordash', role: 'Product Manager', team: 'Product · Referral available',
  },
  {
    icon: 'amazon', role: 'Staff Data Scientist', team: 'Data science',
  },
];

/** Jobs mockup: a slice of recommended positions from the job boards. */
export const JobsMock = () => (
  <div className="mx-auto max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl shadow-indigo-950/10">
    <div className="flex items-baseline justify-between gap-3 border-b border-gray-100 px-5 py-3">
      <strong className="text-sm font-semibold text-gray-900">Recommended for you</strong>
    </div>
    <ul className="divide-y divide-gray-100">
      {jobListings.map(({icon, role, team}) => (
        <li key={`${icon}-${role}`} className="flex items-center gap-3 px-5 py-3.5">
          <img
            src={`/images/companies/icons/${icon}.svg`}
            alt=""
            className="h-7 w-7 shrink-0"
            loading="lazy"
          />
          <span className="min-w-0">
            <strong className="block truncate text-sm font-semibold text-gray-900">{role}</strong>
            <small className="block text-xs text-gray-500">{team}</small>
          </span>
          <ArrowRightIcon className="ml-auto h-4 w-4 shrink-0 text-gray-300" />
        </li>
      ))}
    </ul>
  </div>
);

// Real coaches from /coaching, with photos copied into the repo so the
// mockup never depends on expiring Airtable URLs.
const coaches = [
  {
    initials: 'AD',
    avatar: 'bg-indigo-100 text-indigo-700',
    photo: '/images/coaches/ashley-dennis.jpg',
    name: 'Ashley D.',
    title: 'Product Manager, Meta',
    rating: '4.9 · 100+ sessions',
  },
  {
    initials: 'KV',
    avatar: 'bg-emerald-100 text-emerald-700',
    photo: '/images/coaches/kamesh-vedula.jpg',
    name: 'Kamesh V.',
    title: 'Forward-Deployed Engineer, Rippling',
    rating: '5.0 · 50+ sessions',
  },
  {
    initials: 'VK',
    avatar: 'bg-sky-100 text-sky-700',
    photo: '/images/coaches/venkata-kolli.jpg',
    name: 'Venkata K.',
    title: 'Software Engineer, Amazon',
    rating: '5.0 rating',
  },
];

/** Coach headshot that falls back to an initials circle if it can't load. */
const CoachAvatar = ({photo, initials, avatar}) => {
  const [broken, setBroken] = useState(false);
  if (broken || !photo) {
    return (
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${avatar}`}>
        {initials}
      </span>
    );
  }
  return (
    <img
      src={photo}
      alt=""
      className="h-10 w-10 shrink-0 rounded-full object-cover"
      loading="lazy"
      onError={() => setBroken(true)}
    />
  );
};

/** Coaching mockup: bookable expert coaches with ratings. */
export const CoachingPreview = () => (
  <div className="mx-auto max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl shadow-indigo-950/10">
    <div className="flex items-baseline justify-between gap-3 border-b border-gray-100 px-5 py-3">
      <strong className="text-sm font-semibold text-gray-900">Expert coaches</strong>
    </div>
    <ul className="divide-y divide-gray-100">
      {coaches.map(({
        initials, avatar, photo, name, title, rating,
      }) => (
        <li key={name} className="flex items-center gap-3 px-5 py-3.5">
          <CoachAvatar photo={photo} initials={initials} avatar={avatar} />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold text-gray-900">{name}</span>
            <span className="mt-0.5 block truncate text-xs text-gray-500">{title}</span>
            <span className="mt-0.5 flex items-center gap-1 text-xs font-medium text-gray-600">
              <StarSolidIcon className="h-3.5 w-3.5 text-amber-400" />
              {rating}
            </span>
          </span>
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700">
            <CalendarDaysIcon className="h-3.5 w-3.5 text-gray-400" />
            Book
          </span>
        </li>
      ))}
    </ul>
  </div>
);

const resumeLines = ['w-full', 'w-11/12', 'w-4/5', 'w-full'];

const resumeExamples = [
  {name: 'John Smith', icon: 'google', role: 'Software Engineer'},
  {name: 'Jane Doe', icon: 'meta', role: 'Product Manager'},
  {name: 'Alex Chen', icon: 'anthropic', role: 'Research Engineer'},
];

/** A small anonymized resume page used as a gallery tile. */
const ResumePage = ({name}) => (
  <div className="rounded-lg border border-gray-200 bg-white p-2.5 shadow-sm">
    <span className="block whitespace-nowrap text-[9px] font-bold leading-tight text-gray-900">
      {name}
    </span>
    <span className="mt-1 block h-1 w-14 rounded bg-gray-200" />
    <span className="mt-2.5 block h-1 w-8 rounded bg-gray-300" />
    {resumeLines.map((width, i) => (
      // eslint-disable-next-line react/no-array-index-key
      <span key={i} className={`mt-1 block h-1 rounded bg-gray-100 ${width}`} />
    ))}
    <span className="mt-2.5 block h-1 w-8 rounded bg-gray-300" />
    {resumeLines.slice(0, 3).map((width, i) => (
      // eslint-disable-next-line react/no-array-index-key
      <span key={i} className={`mt-1 block h-1 rounded bg-gray-100 ${width}`} />
    ))}
  </div>
);

/** Resume gallery mockup: browsable resumes that landed real offers. */
export const ResumesPreview = () => (
  <div className="mx-auto max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl shadow-indigo-950/10">
    <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3 sm:gap-2.5">
      {resumeExamples.map(({name, icon, role}, i) => (
        <div
          key={name}
          className={`min-w-0 rounded-xl border border-gray-200 bg-gray-50/70 p-2 ${
            i === 2 ? 'hidden sm:block' : ''
          }`}
        >
          <div className="relative">
            <ResumePage name={name} />
            <span
              className="absolute -right-1.5 -top-1.5 rounded-full bg-white shadow-sm"
              title="Landed an offer"
            >
              <CheckBadgeIcon className="h-4.5 w-4.5 text-emerald-500" />
            </span>
          </div>
          <div className="mt-2 flex items-center gap-1">
            <img src={`/images/companies/icons/${icon}.svg`} alt="" className="h-3 w-3 shrink-0" />
            <span className="whitespace-nowrap text-[9px] font-medium leading-tight text-gray-600">
              {role}
            </span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const guideSteps = [
  {step: 'Recruiter screen', meta: '30 min · Phone'},
  {step: 'Technical phone screen', meta: '45 min · Coding'},
  {step: 'Onsite loop', meta: '4–5 rounds · Coding, system design, behavioral'},
  {step: 'Hiring committee & offer', meta: 'Decision in about two weeks'},
];

/** Company guide mockup: the full interview loop for one company. */
export const GuidePreview = () => (
  <div className="mx-auto max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl shadow-indigo-950/10">
    <div className="flex items-center gap-3 border-b border-gray-100 px-5 py-3">
      <img src="/images/companies/icons/google.svg" alt="" className="h-6 w-6 shrink-0" />
      <strong className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900">
        Google Software Engineer Guide
      </strong>
    </div>
    <ol className="px-5 py-4">
      {guideSteps.map(({step, meta}, i) => (
        <li key={step} className="relative flex gap-3 pb-5 last:pb-0">
          {i < guideSteps.length - 1 && (
            <span className="absolute bottom-0 left-[13px] top-8 w-px bg-gray-200" aria-hidden="true" />
          )}
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-xs font-semibold text-indigo-600">
            {i + 1}
          </span>
          <span className="min-w-0 flex-1 pt-0.5">
            <span className="block truncate text-sm font-semibold text-gray-900">{step}</span>
            <span className="mt-0.5 block truncate text-xs text-gray-500">{meta}</span>
          </span>
        </li>
      ))}
    </ol>
  </div>
);
